import React, { useCallback, useRef } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { useGetStoreDetails } from 'features/store/hooks/use-get-store-details';
import { useIsMobileOrderingAvailable } from 'features/store/hooks/use-is-mobile-ordering-available';
import { useSelectStore } from 'features/store/hooks/use-select-store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { NonTraditionalStoreDialogDrawer } from 'features/store/pages/pickup-store-selector/components/non-traditional-store-dialog-drawer';
import { useNonTraditionalStoreUI } from 'features/store/pages/pickup-store-selector/hooks/use-non-traditional-store';
import { getStoreLocalTime, getUSState } from 'features/store/utils';

import { ILocation } from '@rbi-ctg/frontend';
import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { Helmet } from 'components/helmet';
import { ServiceMode } from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';
import { getStoreSEOSlug } from 'utils/slugify';

import { AmenitiesSection } from './components/amenities-section';
import { Hours } from './components/hours';
import { RestaurantDetailsSection } from './components/restaurant-details-section';
import { SectionDivider } from './components/section-divider';
import { StoreInfoModalHeader } from './components/store-info-modal-header';
import StoreMetaForSEO from './meta';
import { IStoreInfoModalProps } from './types';
import {
  formatStorePhoneNumber,
  getFormattedStoreAddress,
  getStoreAmenityItems,
  getStoreServiceAvailabilityItems,
} from './utils';

export const StoreInfoModal = ({ store }: IStoreInfoModalProps) => {
  const {
    name: storeName,
    phoneNumber,
    restaurantImage,
    latitude: lat,
    longitude: lng,
    physicalAddress,
  } = store;
  const { StoreChangeDialog } = useStoreContext();
  const nonTraditionalStoreUI = useNonTraditionalStoreUI({ restaurant: store });
  const router = useRouter();

  const { current: position } = useRef({ lat, lng });
  const mapPosition = position as ILocation;
  const { activeCoordinates } = useGeolocation();

  const {
    fetchingPosData,
    reorder: { reordering },
  } = useOrderContext();
  const selectStore = useSelectStore();

  const streetAddress = physicalAddress?.address1;
  const formattedCity = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = physicalAddress?.postalCode?.split('-')[0];
  const { isStoreAvailableForPickup, isServiceModeCapable } = useStoreAvailabilityFns();

  const canonicalUrl = [`${routes.store}`, getStoreSEOSlug(store)].join('/');
  const storeLocalTime = getStoreLocalTime();

  const shouldShowDineIn = isServiceModeCapable(store, ServiceMode.EAT_IN, storeLocalTime);
  const shouldShowTakeout = isServiceModeCapable(store, ServiceMode.TAKEOUT, storeLocalTime);
  const shouldShowMobileOrderDriveThru = isServiceModeCapable(
    store,
    ServiceMode.MOBILE_ORDER_DRIVE_THRU,
    storeLocalTime
  );
  const shouldShowDriveThru = isServiceModeCapable(store, ServiceMode.DRIVE_THRU, storeLocalTime);
  const formattedAddress = getFormattedStoreAddress(physicalAddress);
  const formattedPhoneNumber = formatStorePhoneNumber(phoneNumber);
  const isMobileOrderingAvailableForStore = useIsMobileOrderingAvailable(store);
  const serviceAvailabilityItems = getStoreServiceAvailabilityItems(store);
  const amenityItems = getStoreAmenityItems(store, isMobileOrderingAvailableForStore);
  const shouldShowAmenitiesSection = amenityItems.length > 0;

  const storeHoursBreakdown: [
    boolean,
    TranslationKey,
    'diningRoomHours' | 'driveThruHours' | 'mobileOrderDriveThruHours'
  ][] = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours', 'driveThruHours'],
    [shouldShowMobileOrderDriveThru, 'mobileOrderDriveThruHours', 'mobileOrderDriveThruHours'],
  ];

  const isStoreAvailable = isStoreAvailableForPickup(store);

  const orderButtonEnabled = isStoreAvailable && !fetchingPosData;

  const handleOnSelectStore = useCallback(async () => {
    if (nonTraditionalStoreUI?.isNonTraditionalStore) {
      nonTraditionalStoreUI.openDrawer();
      return;
    }
    await selectStore({
      newStore: store,
    });
  }, [nonTraditionalStoreUI, selectStore, store]);

  const handleOnConfirmNonTraditionalStoreDialog = async () => {
    await selectStore({
      newStore: store,
    });
    nonTraditionalStoreUI?.closeDrawer();
  };

  // Loading state covers two scenarios:
  // - fetchingPosData: Loading store POS connectivity/menu data for regular ordering
  // - reordering: Processing a reorder request (users may be redirected here to select a store if no store selected)
  const isLoading = fetchingPosData || reordering;

  return (
    <CardModal
      testID="store-info-modal"
      analyticsEvent="Store Details"
      scrollView
      _headerOverride={
        <StoreInfoModalHeader
          addressLine={physicalAddress?.address1}
          mapPosition={mapPosition}
          onClose={() => router.dismiss()}
          restaurantImage={restaurantImage}
          storeName={storeName}
        />
      }
      footer={
        isStoreAvailable && (
          <ActionButton
            isLoading={isLoading}
            testID="store-modal-order-here"
            fullWidth
            disabled={!orderButtonEnabled}
            onPress={handleOnSelectStore}
          >
            {t('orderHere')}
          </ActionButton>
        )
      }
    >
      {/* @ts-ignore */}
      <Helmet testID="canonical" link={[{ rel: 'canonical', href: canonicalUrl }]} />
      <RestaurantDetailsSection
        activeCoordinates={activeCoordinates}
        formattedAddress={formattedAddress}
        formattedPhoneNumber={formattedPhoneNumber}
        mapPosition={mapPosition}
        phoneNumber={phoneNumber}
        serviceAvailabilityItems={serviceAvailabilityItems}
      />
      <SectionDivider />
      {shouldShowAmenitiesSection && (
        <>
          <AmenitiesSection items={amenityItems} />
          <SectionDivider />
        </>
      )}
      {storeHoursBreakdown.map(
        ([shouldShow, messageId, hours]) =>
          shouldShow && <Hours key={messageId} title={t(messageId)} hours={store[hours]} />
      )}
      <StoreMetaForSEO
        atCopy={t('at')}
        streetAddress={streetAddress ?? ''}
        city={formattedCity ?? ''}
        state={state}
        postalCode={postalCode ?? ''}
        phoneNumber={phoneNumber ?? ''}
        image={(restaurantImage as any) ?? undefined}
      />
      <StoreChangeDialog heading={t('changeStores')} />
      {nonTraditionalStoreUI && (
        <NonTraditionalStoreDialogDrawer
          onConfirm={handleOnConfirmNonTraditionalStoreDialog}
          onCancel={nonTraditionalStoreUI.closeDrawer}
          isOpen={nonTraditionalStoreUI.isDialogDrawerOpen}
          accessInstructions={nonTraditionalStoreUI.accessInstructions}
        />
      )}
    </CardModal>
  );
};

const StoreInfoModalContainer = () => {
  const { id } = useLocalSearchParams<{ id?: string }>();
  const { restaurant, error, loading } = useGetStoreDetails(id);
  const [ErrorDialog] = useErrorModal({
    init: true,
    modalAppearanceEventMessage: 'Error: Fetching Restaurant Info Failure',
  });

  if (error) {
    return <ErrorDialog />;
  }

  const isLoading = loading || !restaurant;

  if (isLoading) {
    return (
      <CardModal>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return <StoreInfoModal store={restaurant} />;
};

export default StoreInfoModalContainer;
